import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  UpgradeAccountModal,
  NoActivationNeededModal,
  CertMasterAuthenticateModal,
  UpgradeSuccessModal,
  LinkedSuccessfullyModal,
  ErrorModal,
} from 'components';
import { LoadingOverlay } from '@comptia-sso/core';
import { useCertMasterPlatformRedemption } from './CertMasterPlatformRedemptionContext';
import { useProfile, useProfileEntitlements } from 'hooks';

export function CertMasterPlatformRedemption() {
  const [profile, { refetch: refetchProfile }] = useProfile();
  const [, { refetch: refetchProfileEntitlements }] = useProfileEntitlements(
    profile?.id,
  );
  const { t } = useTranslation();
  const redemption = useCertMasterPlatformRedemption();
  const [showAlreadyActivatedModal, setShowAlreadyActivatedModal] = useState(
    redemption.hasProductAlready,
  );
  const [showUpgradeAccountModal, setShowUpgradeAccountModal] = useState(
    redemption.hasPossibleCertMasterAccount,
  );
  const [showCertMasterAuthenticateModal, setShowCertMasterAuthenticateModal] =
    useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showLinkedSuccess, setShowLinkedSuccess] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setShowAlreadyActivatedModal(redemption.hasProductAlready);
    setShowUpgradeAccountModal(redemption.needToLinkToCertMaster);
  }, [redemption]);

  async function handleLinked(): Promise<void> {
    setLoading(true);
    setShowUpgradeAccountModal(false);
    setShowCertMasterAuthenticateModal(false);

    if (redemption.accessKey == null) return;

    const result = await redemption.redeemAccessKey(
      redemption.accessKey,
      false,
    );

    if (result.success) setShowSuccessModal(true);
    else {
      setErrorMessage(result.errorMessage);
      setShowErrorModal(true);
      refetchProfile();
    }

    setLoading(false);
  }

  async function handleCreateAccount(): Promise<void> {
    setLoading(true);
    setShowUpgradeAccountModal(false);
    if (redemption.accessKey == null) return;

    const result = await redemption.redeemAccessKey(
      redemption.accessKey,
      false,
    );

    if (result.success) setShowSuccessModal(true);
    else {
      setErrorMessage(result.errorMessage);
      setShowErrorModal(true);
      refetchProfile();
    }

    setLoading(false);
  }

  return (
    <>
      <NoActivationNeededModal
        isOpen={showAlreadyActivatedModal}
        onClose={() => {
          setShowErrorModal(false);
          setShowAlreadyActivatedModal(false);
        }}
        products={redemption.activatedProducts}
      />
      <UpgradeAccountModal
        isOpen={showUpgradeAccountModal}
        email={
          redemption.hasPossibleCertMasterAccount ? profile.email : undefined
        }
        onConnectAccount={() => setShowCertMasterAuthenticateModal(true)}
        onCreateAccount={handleCreateAccount}
        onClose={() => setShowUpgradeAccountModal(false)}
      />
      <CertMasterAuthenticateModal
        isShowing={showCertMasterAuthenticateModal}
        onClose={() => {
          setShowCertMasterAuthenticateModal(false);
          setShowUpgradeAccountModal(true);
        }}
        onLinked={handleLinked}
      />
      <UpgradeSuccessModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          refetchProfile();
          refetchProfileEntitlements();
        }}
      />
      <LinkedSuccessfullyModal
        isOpen={showLinkedSuccess}
        onClose={() => {
          setShowLinkedSuccess(false);
          refetchProfile();
          refetchProfileEntitlements();
        }}
      />
      <ErrorModal
        isOpen={!showAlreadyActivatedModal && showErrorModal}
        errorMessage={errorMessage}
        onClose={() => {
          setShowErrorModal(false);
        }}
      />
      <LoadingOverlay isOpen={loading} text={t('Loading.RedeemingAccessKey')} />
    </>
  );
}
